import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  effect,
  forwardRef,
  inject,
  input,
  AfterViewInit,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { InputComponent } from '@purolator/ui-kit-angular';
import {
  EMPTY,
  catchError,
  debounceTime,
  distinctUntilChanged,
  from,
  map,
  switchMap,
  tap,
} from 'rxjs';
import { AddressValidationApiService } from '../../../core/api/address-validation-api.service';
import {
  ADDRESS_AUTOCOMPLETE_DEBOUNCE_MS,
  ADDRESS_AUTOCOMPLETE_DEFAULT_COUNTRY,
  ADDRESS_AUTOCOMPLETE_DEFAULT_MAX_ITEMS,
  ADDRESS_AUTOCOMPLETE_MIN_SEARCH_LENGTH,
  type AddressAutocompleteItem,
  type AddressAutocompleteSelection,
} from '../../../core/models/address-autocomplete';
import { TranslationService, TranslatePipe } from '../../../core/i18n';
import {
  buildAddressAutocompleteHighlightSegments,
  isAddressAutocompleteFindItem,
  type AddressAutocompleteTextSegment,
} from '../../../pages/ship-from-ship-to/utils/address-autocomplete-display';
import { ADDRESS_VALIDATION_STREET_BROWSER_AUTOFILL } from '../../../shared/constants/address-validation-browser-autofill';
import { dedupeAddressAutocompleteItems } from '../../../pages/ship-from-ship-to/utils/dedupe-address-autocomplete-items';

/**
 * Returns true when an autocomplete request was aborted before completion.
 */
function isAutocompleteAbortError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const candidate = error as { code?: string; name?: string };
  return candidate.code === 'ERR_CANCELED' || candidate.name === 'CanceledError';
}

/**
 * Street address input with debounced Canada Post autocomplete suggestions dropdown.
 */
@Component({
  selector: 'app-street-address-autocomplete-field',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.street-address-autocomplete-field-host--full-width]': 'fullWidth()',
  },
  imports: [ReactiveFormsModule, InputComponent, TranslatePipe],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StreetAddressAutocompleteField),
      multi: true,
    },
  ],
  templateUrl: './street-address-autocomplete-field.html',
  styleUrl: './street-address-autocomplete-field.scss',
})
export class StreetAddressAutocompleteField
  implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy
{
  private readonly destroyRef = inject(DestroyRef);
  private readonly addressValidationApi = inject(AddressValidationApiService);
  private readonly translation = inject(TranslationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  readonly label = input('');
  readonly required = input(false);
  readonly fullWidth = input(false);
  readonly error = input(false);
  readonly errorMessage = input('');
  readonly listboxLabel = input('Address suggestions');
  /** Optional native input name to reduce browser autofill heuristics. */
  readonly inputName = input('');
  /** Extra element ids merged into the street input aria-describedby list. */
  readonly describedByIds = input<readonly string[]>([]);
  /** Emitted when the customer selects a suggestion from the dropdown. */
  readonly suggestionSelected = output<AddressAutocompleteSelection>();

  protected readonly streetAddressControl = new FormControl('', { nonNullable: true });
  protected readonly suggestions = signal<AddressAutocompleteItem[]>([]);
  protected readonly activeOptionIndex = signal(-1);
  protected readonly isDropdownOpen = signal(false);
  protected readonly showNoResults = signal(false);
  /** True when autocomplete failed and the customer should enter the address manually. */
  protected readonly suggestionsUnavailable = signal(false);

  private static nextId = 0;
  protected readonly fieldId = `street-address-autocomplete-${StreetAddressAutocompleteField.nextId++}`;

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private disabled = false;
  private nativeInput: HTMLInputElement | null = null;
  private autocompleteAbortController: AbortController | null = null;
  private lastSearchTerm = '';

  private readonly inputKeyDownHandler = (event: KeyboardEvent): void => {
    this.onInputKeyDown(event);
  };

  private readonly inputBlurHandler = (): void => {
    this.onInputBlur();
  };

  private readonly clickOutsideHandler = (event: Event): void => {
    const path =
      typeof event.composedPath === 'function'
        ? event.composedPath()
        : [event.target as EventTarget];

    window.setTimeout(() => {
      if (path.includes(this.elementRef.nativeElement)) {
        return;
      }
      this.closeDropdown();
    }, 0);
  };

  constructor() {
    effect(() => {
      this.describedByIds();
      this.error();
      this.errorMessage();
      queueMicrotask(() => this.syncComboboxAria());
    });

    this.destroyRef.onDestroy(() => {
      this.autocompleteAbortController?.abort();
    });
  }

  /**
   * Subscribes to input changes and loads autocomplete suggestions after debounce.
   */
  ngOnInit(): void {
    document.addEventListener('mousedown', this.clickOutsideHandler);

    this.streetAddressControl.valueChanges
      .pipe(
        tap((value) => {
          this.onChange(value);
        }),
        debounceTime(ADDRESS_AUTOCOMPLETE_DEBOUNCE_MS),
        map((value) => value.trim()),
        distinctUntilChanged(),
        switchMap((searchTerm) => {
          if (searchTerm.length < ADDRESS_AUTOCOMPLETE_MIN_SEARCH_LENGTH) {
            this.clearSuggestions();
            this.suggestionsUnavailable.set(false);
            return EMPTY;
          }

          this.lastSearchTerm = searchTerm;

          this.autocompleteAbortController?.abort();
          const abortController = new AbortController();
          this.autocompleteAbortController = abortController;

          return from(
            this.addressValidationApi.autocomplete(
              {
                searchTerm,
                country: ADDRESS_AUTOCOMPLETE_DEFAULT_COUNTRY,
                languagePreference: this.translation.locale(),
                maxItems: ADDRESS_AUTOCOMPLETE_DEFAULT_MAX_ITEMS,
              },
              { signal: abortController.signal },
            ),
          ).pipe(
            catchError((error) => {
              if (isAutocompleteAbortError(error)) {
                return EMPTY;
              }

              this.clearSuggestions();
              this.suggestionsUnavailable.set(true);
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((items) => {
        this.applyAutocompleteResults(items);
      });
  }

  /**
   * Wires keyboard and focus handlers on the kit input's native element for combobox ARIA.
   */
  ngAfterViewInit(): void {
    this.nativeInput = this.getNativeInput();
    if (!this.nativeInput) {
      return;
    }

    this.nativeInput.addEventListener('keydown', this.inputKeyDownHandler);
    this.nativeInput.addEventListener('blur', this.inputBlurHandler);
    this.nativeInput.setAttribute('autocomplete', ADDRESS_VALIDATION_STREET_BROWSER_AUTOFILL);
    this.syncComboboxAria();

    this.destroyRef.onDestroy(() => {
      this.nativeInput?.removeEventListener('keydown', this.inputKeyDownHandler);
      this.nativeInput?.removeEventListener('blur', this.inputBlurHandler);
    });
  }

  /**
   * Removes the document click listener used to close the dropdown.
   */
  ngOnDestroy(): void {
    document.removeEventListener('mousedown', this.clickOutsideHandler);
  }

  /**
   * Writes the parent form value into the nested street address control.
   */
  writeValue(value: string | null): void {
    this.streetAddressControl.setValue(value ?? '', { emitEvent: false });
    this.cdr.markForCheck();
  }

  /**
   * Registers the parent form change handler.
   */
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  /**
   * Registers the parent form touched handler.
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Enables or disables the nested street address control.
   */
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.streetAddressControl.disable({ emitEvent: false });
      this.clearSuggestions();
      return;
    }

    this.streetAddressControl.enable({ emitEvent: false });
    this.cdr.markForCheck();
  }

  /**
   * Returns the stable DOM id for the suggestions listbox.
   */
  protected get listboxId(): string {
    return `${this.fieldId}-listbox`;
  }

  /**
   * Returns the active option id for aria-activedescendant, when the listbox is open.
   */
  protected get activeDescendantId(): string | null {
    if (!this.isDropdownOpen() || this.activeOptionIndex() < 0) {
      return null;
    }

    return this.optionId(this.activeOptionIndex());
  }

  /**
   * Builds highlighted text segments for one autocomplete suggestion row.
   */
  protected highlightSegments(item: AddressAutocompleteItem): AddressAutocompleteTextSegment[] {
    return buildAddressAutocompleteHighlightSegments(item.text, item.highlight);
  }

  /**
   * Returns the stable DOM id for a dropdown option at the given index.
   */
  protected optionId(index: number): string {
    return `${this.fieldId}-option-${index}`;
  }

  /**
   * Marks the field as touched when the customer leaves the street input.
   */
  protected onInputBlur(): void {
    this.onTouched();
  }

  /**
   * Navigates and selects suggestions from the keyboard, mirroring FloatingSelect behavior.
   */
  protected onInputKeyDown(event: KeyboardEvent): void {
    if (this.disabled) {
      return;
    }

    if (this.showNoResults()) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDropdown();
      }
      return;
    }

    const items = this.suggestions();

    switch (event.key) {
      case 'ArrowDown':
        this.handleArrowDown(event, items);
        break;
      case 'ArrowUp':
        this.handleArrowUp(event, items);
        break;
      case 'Enter':
        if (!this.isDropdownOpen() || this.activeOptionIndex() < 0) {
          return;
        }
        event.preventDefault();
        this.applySelectedSuggestion(items[this.activeOptionIndex()]);
        break;
      case 'Escape':
        if (!this.isDropdownOpen()) {
          return;
        }
        event.preventDefault();
        this.closeDropdown();
        break;
      case 'Home':
        this.handleEdgeNavigation(event, items, 0);
        break;
      case 'End':
        this.handleEdgeNavigation(event, items, items.length - 1);
        break;
      case 'Tab':
        if (this.isDropdownOpen()) {
          this.closeDropdown();
        }
        break;
      default:
        break;
    }
  }

  /** Handles ArrowDown: opens dropdown or moves active option forward. */
  private handleArrowDown(event: KeyboardEvent, items: AddressAutocompleteItem[]): void {
    if (items.length === 0) {
      return;
    }
    event.preventDefault();
    if (!this.isDropdownOpen()) {
      this.openDropdown(items);
    } else {
      this.updateActiveOptionIndex(Math.min(this.activeOptionIndex() + 1, items.length - 1));
    }
    this.cdr.markForCheck();
  }

  /** Handles ArrowUp: moves active option backward when dropdown is open. */
  private handleArrowUp(event: KeyboardEvent, items: AddressAutocompleteItem[]): void {
    if (!this.isDropdownOpen() || items.length === 0) {
      return;
    }
    event.preventDefault();
    this.updateActiveOptionIndex(Math.max(this.activeOptionIndex() - 1, 0));
    this.cdr.markForCheck();
  }

  /** Handles Home/End: jumps to first or last option when dropdown is open. */
  private handleEdgeNavigation(
    event: KeyboardEvent,
    items: AddressAutocompleteItem[],
    targetIndex: number,
  ): void {
    if (!this.isDropdownOpen() || items.length === 0) {
      return;
    }
    event.preventDefault();
    this.updateActiveOptionIndex(targetIndex);
    this.cdr.markForCheck();
  }

  /**
   * Emits the selected suggestion to the population layer.
   */
  protected selectSuggestion(item: AddressAutocompleteItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.applySelectedSuggestion(item);
  }

  /**
   * Updates keyboard/mouse highlight state for a dropdown option.
   */
  protected setActiveOption(index: number): void {
    this.updateActiveOptionIndex(index);
    this.cdr.markForCheck();
  }

  /**
   * Applies combobox/listbox ARIA attributes on the kit input's native element.
   */
  private syncComboboxAria(): void {
    const input = this.nativeInput ?? this.getNativeInput();
    if (!input) {
      return;
    }

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-expanded', String(this.isDropdownOpen()));

    if (this.isDropdownOpen()) {
      input.setAttribute('aria-controls', this.listboxId);
    } else {
      input.removeAttribute('aria-controls');
    }

    const activeDescendantId = this.activeDescendantId;
    if (activeDescendantId) {
      input.setAttribute('aria-activedescendant', activeDescendantId);
    } else {
      input.removeAttribute('aria-activedescendant');
    }

    this.syncDescribedBy(input);
  }

  /**
   * Merges kit error helper ids with optional external description ids on the street input.
   */
  private syncDescribedBy(input: HTMLInputElement): void {
    const ids: string[] = [];

    if (this.error() && this.errorMessage().trim().length > 0) {
      ids.push(`${this.fieldId}-helper`);
    }

    for (const id of this.describedByIds()) {
      if (id.trim().length > 0) {
        ids.push(id.trim());
      }
    }

    if (ids.length > 0) {
      input.setAttribute('aria-describedby', ids.join(' '));
      return;
    }

    input.removeAttribute('aria-describedby');
  }

  /**
   * Returns the native text input rendered inside puro-input.
   */
  private getNativeInput(): HTMLInputElement | null {
    return this.elementRef.nativeElement.querySelector('.puro-input__field');
  }

  /**
   * Opens the suggestion list with the first option highlighted.
   */
  private openDropdown(items: AddressAutocompleteItem[]): void {
    this.isDropdownOpen.set(items.length > 0);
    this.showNoResults.set(false);
    this.updateActiveOptionIndex(items.length > 0 ? 0 : -1);
  }

  /**
   * Updates the highlighted option and keeps it visible inside the scrollable dropdown.
   */
  private updateActiveOptionIndex(index: number): void {
    this.activeOptionIndex.set(index);
    this.syncComboboxAria();
    this.scrollActiveOptionIntoView();
  }

  /**
   * Scrolls the highlighted suggestion into view inside the dropdown listbox.
   */
  private scrollActiveOptionIntoView(): void {
    const index = this.activeOptionIndex();
    if (index < 0 || !this.isDropdownOpen()) {
      return;
    }

    queueMicrotask(() => {
      const option = this.elementRef.nativeElement.querySelector(
        `#${this.optionId(index)}`,
      ) as HTMLElement | null;
      if (typeof option?.scrollIntoView === 'function') {
        option.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  /**
   * Emits the selected suggestion to the population layer, or drills down for Find containers.
   */
  private applySelectedSuggestion(item: AddressAutocompleteItem): void {
    if (isAddressAutocompleteFindItem(item)) {
      void this.drillDownFromFindItem(item);
      return;
    }

    this.clearSuggestions();
    this.suggestionSelected.emit({
      id: item.id,
      text: item.text,
      description: item.description,
      next: item.next,
    });
  }

  /**
   * Applies deduped autocomplete results to the dropdown and combobox ARIA state.
   */
  private applyAutocompleteResults(items: AddressAutocompleteItem[]): void {
    const dedupedItems = dedupeAddressAutocompleteItems(items);
    this.suggestionsUnavailable.set(false);
    this.suggestions.set(dedupedItems);
    this.showNoResults.set(dedupedItems.length === 0);
    this.isDropdownOpen.set(dedupedItems.length > 0 || this.showNoResults());
    this.updateActiveOptionIndex(dedupedItems.length > 0 ? 0 : -1);
    this.cdr.markForCheck();
  }

  /**
   * Loads child suggestions for a Canada Post Find container using LastId drill-down.
   */
  private async drillDownFromFindItem(item: AddressAutocompleteItem): Promise<void> {
    this.autocompleteAbortController?.abort();
    const abortController = new AbortController();
    this.autocompleteAbortController = abortController;

    try {
      const items = await this.addressValidationApi.autocomplete(
        {
          searchTerm: this.lastSearchTerm,
          country: ADDRESS_AUTOCOMPLETE_DEFAULT_COUNTRY,
          languagePreference: this.translation.locale(),
          maxItems: ADDRESS_AUTOCOMPLETE_DEFAULT_MAX_ITEMS,
          lastId: item.id,
        },
        { signal: abortController.signal },
      );
      this.applyAutocompleteResults(items);
    } catch (error) {
      if (!isAutocompleteAbortError(error)) {
        this.clearSuggestions();
        this.suggestionsUnavailable.set(true);
      }
    }
  }

  /**
   * Clears suggestions and closes the dropdown.
   */
  private clearSuggestions(): void {
    this.suggestions.set([]);
    this.showNoResults.set(false);
    this.activeOptionIndex.set(-1);
    this.isDropdownOpen.set(false);
    this.syncComboboxAria();
    this.cdr.markForCheck();
  }

  /**
   * Hides the suggestion list without changing the current input value.
   */
  private closeDropdown(): void {
    this.isDropdownOpen.set(false);
    this.showNoResults.set(false);
    this.activeOptionIndex.set(-1);
    this.syncComboboxAria();
    this.cdr.markForCheck();
  }
}
